import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { ChevronRight, Users, Target, TrendingUp, Heart, Award, Globe, ArrowRight, Home, Shield, Stethoscope, BookOpen, MapPin, Star, Baby, GraduationCap, HandHeart } from "lucide-react";

function Dress() {
  const [isVisible, setIsVisible] = useState({});
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeSection, setActiveSection] = useState('crisis');

  const heroImages = [
    "img/dress/13.jpg",
    "img/dress/10.jpeg", 
    "img/dress/11.jpeg"
  ];

  const galleryImages = [
    "img/dress/10-min.jpg",
    "img/dress/3-min.jpg",
    "img/dress/4-min.jpg",
    "img/dress/5-min.jpg",
    "img/dress/6-min.jpg",
    "img/dress/7-min.jpg"
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    const imageInterval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000);

    return () => {
      observer.disconnect();
      clearInterval(imageInterval);
    };
  }, []);

  const stats = [
    { icon: Users, number: "90%", label: "Living in Poverty", description: "Population in hand-to-mouth survival mode", color: "from-red-500 to-orange-500" },
    { icon: MapPin, number: "5+", label: "Target Areas", description: "Banda, Kambantanyi, Busaana, Kayunga, Nakatovu", color: "from-blue-500 to-cyan-500" },
    { icon: Baby, number: "↑High", label: "Adolescent Pregnancies", description: "Due to poverty and lack of education", color: "from-purple-500 to-pink-500" },
    { icon: Home, number: "LDC", label: "Least Developed Country", description: "Limited access to basic necessities", color: "from-green-500 to-emerald-500" }
  ];

  const interventions = [
    {
      icon: HandHeart,
      title: "Basic Necessities Provision",
      description: "Distributing clothes, shoes, soap, and essential medicines to vulnerable families",
      impact: "Immediate Relief",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Stethoscope,
      title: "Healthcare Infrastructure",
      description: "Building staff quarters for Nakatovu Health Center and providing delivery beds",
      impact: "Long-term Health Access",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: BookOpen,
      title: "Reproductive Health Education",
      description: "Comprehensive family planning services and sexual reproductive health education",
      impact: "Cycle Breaking",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: GraduationCap,
      title: "Educational Empowerment",
      description: "Promoting girls' education as a pathway out of poverty and early pregnancy",
      impact: "Future Transformation",
      color: "from-orange-500 to-red-500"
    }
  ];

  const sections = [
    { id: 'crisis', label: 'The Crisis', icon: Target },
    { id: 'solution', label: 'Our Response', icon: Heart },
    { id: 'impact', label: 'Creating Change', icon: Award },
    { id: 'community', label: 'Community Focus', icon: Users }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-50">
      <Header />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden pt-20 sm:pt-32 pb-12 sm:pb-20">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-orange-200/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-48 h-48 bg-red-200/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-yellow-200/30 rounded-full blur-xl animate-pulse delay-2000"></div>
          
          {/* Decorative heart pattern */}
          <div className="absolute top-1/3 right-1/4 w-96 h-96 opacity-5">
            <svg viewBox="0 0 100 100" className="w-full h-full text-red-600 animate-pulse">
              <path d="M50,85 C50,85 20,65 20,45 C20,35 28,25 40,25 C45,25 50,30 50,30 C50,30 55,25 60,25 C72,25 80,35 80,45 C80,65 50,85 50,85 Z" fill="currentColor"/>
            </svg>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div 
              id="hero-content"
              data-animate
              className={`transform transition-all duration-1000 mt-20 sm:mt-0 text-center sm:text-left px-4 sm:px-0 ${
                isVisible['hero-content'] ? 'translate-x-0 opacity-100' : 'lg:-translate-x-full opacity-0'
              }`}
            >
              <div className="inline-flex items-center bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <HandHeart className="w-4 h-4 mr-2" />
                Humanitarian Initiative
              </div>
              
              <h1 className="text-4xl md:text-6xl font-black mb-6">
                <span className="bg-gradient-to-r from-orange-600 via-red-600 to-red-800 bg-clip-text text-transparent">
                  Dress The Soul
                </span>
                <br />
                <span className="text-gray-800">Project</span>
              </h1>
              
              <div className="bg-gradient-to-r from-orange-50 to-red-50 border-l-4 border-red-500 p-6 rounded-r-2xl mb-8">
                <p className="text-lg text-gray-700 font-medium">
                  <span className="font-bold text-red-800">Restoring dignity</span> through basic necessities. 
                  We're addressing extreme poverty in Kayunga District where 90% of the population 
                  lives in survival mode.
                </p>
              </div>
              
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                More than providing clothing—we're breaking cycles of poverty, preventing adolescent pregnancies, 
                and building healthcare infrastructure in Uganda's most vulnerable communities.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => window.location.href = '/fund-our-work'}
                  className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white px-8 py-4 rounded-2xl font-semibold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg flex items-center justify-center gap-2"
                >
                  Support This Mission <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            
            {/* Image Carousel */}
            <div
              id="hero-image"
              data-animate
              className={`transform transition-all duration-1000 delay-300 ${isVisible['hero-image'] ? 'translate-x-0 opacity-100' : 'lg:translate-x-full opacity-0'
                }`}
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <div className="relative h-96 md:h-[500px]">
                  <img
                    src={heroImages[currentImageIndex]}
                    alt="Dress The Soul Project"
                    className="w-full h-full object-cover transition-opacity duration-1000"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                  
                  {/* Overlay badge */}
                  <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm rounded-2xl px-4 py-2">
                    <div className="flex items-center gap-2">
                      <Heart className="w-5 h-5 text-red-600" />
                      <span className="font-semibold text-gray-800">Dignity Restoration</span>
                    </div>
                  </div>
                </div>
                
                {/* Image indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {heroImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentImageIndex ? 'bg-white scale-125' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="py-20 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="stats-header"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['stats-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              The Poverty <span className="bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">Reality</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding the scope of extreme poverty and its impact on Kayunga District communities
            </p>
            <div className="w-32 h-1 bg-gradient-to-r from-red-500 to-orange-500 rounded-full mx-auto mt-6"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  id={`stat-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 delay-${index * 200} ${
                    isVisible[`stat-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                >
                  <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-red-500">
                    <div className={`w-16 h-16 bg-gradient-to-r ${stat.color} rounded-2xl flex items-center justify-center mb-6 mx-auto`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-center">
                      <div className="text-4xl font-black text-gray-800 mb-2">{stat.number}</div>
                      <div className="text-lg font-semibold text-red-600 mb-2">{stat.label}</div>
                      <div className="text-sm text-gray-600">{stat.description}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Interactive Content Sections */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Section Navigation */}
          <div className="flex flex-wrap justify-center mb-12">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold text-lg transition-all duration-300 mx-2 mb-2 ${
                    activeSection === section.id 
                      ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-lg transform scale-105' 
                      : 'bg-white text-gray-600 hover:bg-gray-50 shadow'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {section.label}
                </button>
              );
            })}
          </div>

          {/* Dynamic Content */}
          <div className="transition-all duration-500">
            {activeSection === 'crisis' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-3xl font-bold text-gray-800 mb-6">
                    Extreme Poverty's <span className="bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">Devastating Cycle</span>
                  </h3>
                  <div className="space-y-6 text-lg text-gray-700">
                    <p>
                      Kayunga District stands as one of Uganda's poorest regions, where <strong>90% of the population</strong> 
                      survives hand-to-mouth, unable to afford basic necessities like clothing, healthcare, or education.
                    </p>
                    
                    <div className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 p-6 rounded-r-2xl">
                      <p className="font-semibold text-red-800">
                        This dire poverty drives population growth, adolescent pregnancies, school dropouts, 
                        and perpetuates generational cycles of hardship.
                      </p>
                    </div>
                    
                    <p>
                      In areas like Busaana Subcounty, vulnerable young girls face exploitation, 
                      resulting in pregnancies where fathers cannot provide even basic human necessities. 
                      Families lack adequate clothing, healthcare access, and essential services.
                    </p>
                  </div>
                </div>
                <div className="relative">
                  <img
                    src="img/dress/13.jpg"
                    alt="Community poverty"
                    className="w-full h-80 object-cover rounded-3xl shadow-2xl"
                  />
                  
                  {/* Floating impact metrics */}
                  <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-lg">
                    <div className="text-2xl font-bold text-red-600">90%</div>
                    <div className="text-sm text-gray-600">In Survival Mode</div>
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'solution' && (
              <div>
                <h3 className="text-3xl font-bold text-gray-800 mb-12 text-center">
                  Our Comprehensive <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">Intervention</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {interventions.map((intervention, index) => {
                    const Icon = intervention.icon;
                    return (
                      <div key={index} className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                        <div className={`w-16 h-16 bg-gradient-to-r ${intervention.color} rounded-2xl flex items-center justify-center mb-6`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <h4 className="text-xl font-bold text-gray-800 mb-4">{intervention.title}</h4>
                        <p className="text-gray-600 mb-4">{intervention.description}</p>
                        <div className="inline-flex items-center bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-semibold">
                          <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                          {intervention.impact}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeSection === 'impact' && (
              <div className="text-center">
                <h3 className="text-3xl font-bold text-gray-800 mb-8">
                  Transformative <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Outcomes</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                  <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-3xl p-8 text-white">
                    <div className="text-4xl font-black mb-4">✓</div>
                    <div className="text-lg font-semibold mb-2">Healthcare Infrastructure</div>
                    <div className="text-blue-100">Nakatovu Health Center staff quarters constructed</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-3xl p-8 text-white">
                    <div className="text-4xl font-black mb-4">↓</div>
                    <div className="text-lg font-semibold mb-2">Maternal Mortality</div>
                    <div className="text-green-100">Delivery beds improving birth safety</div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-3xl p-8 text-white">
                    <div className="text-4xl font-black mb-4">↑</div>
                    <div className="text-lg font-semibold mb-2">Dignity Restoration</div>
                    <div className="text-purple-100">Basic necessities reaching vulnerable families</div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-3xl p-8">
                  <h4 className="text-2xl font-bold text-gray-800 mb-4">Beyond Immediate Relief</h4>
                  <p className="text-lg text-gray-700">
                    The Dress The Soul Project represents more than emergency aid—it's a comprehensive approach 
                    to breaking poverty cycles through dignity restoration, healthcare infrastructure development, 
                    and educational empowerment that creates lasting community transformation.
                  </p>
                </div>
              </div>
            )}

            {activeSection === 'community' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="relative">
                  <img
                    src="img/dress/11.jpeg"
                    alt="Community development"
                    className="w-full h-80 object-cover rounded-3xl shadow-2xl"
                  />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-gray-800 mb-6">
                    Strategic <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Community Focus</span>
                  </h3>
                  <div className="space-y-6 text-lg text-gray-700">
                    <p>
                      Our work spans the most vulnerable areas: <strong>Banda and Kambantanyi landing sites, 
                      Busaana, Kayunga Sub County, and Nakatovu</strong>—each requiring tailored interventions 
                      based on specific community needs.
                    </p>
                    
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-500 p-6 rounded-r-2xl">
                      <p className="font-semibold text-purple-800">
                        Through partnerships and deep community engagement, we create sustainable change 
                        that addresses root causes rather than just symptoms of poverty.
                      </p>
                    </div>
                    
                    <p>
                      Our approach integrates immediate relief with long-term development: while we provide 
                      essential clothes and medicines today, we simultaneously build healthcare facilities 
                      and educational programs that will serve communities for generations.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Photo Gallery Section */}
      <div className="py-20 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Project <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">Impact</span>
            </h2>
            <p className="text-xl text-gray-600">Documenting dignity restoration and community transformation</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image, index) => (
              <div key={index} className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                <img
                  src={image}
                  alt={`Dress The Soul project impact ${index + 1}`}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 text-white">
                    <div className="font-semibold">Community Support</div>
                    <div className="text-sm text-gray-200">Dress The Soul Initiative</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="py-20 bg-gradient-to-r from-red-600 via-orange-600 to-yellow-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <div 
            id="cta-content"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['cta-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full text-lg font-semibold mb-8">
              <HandHeart className="w-5 h-5 mr-3" />
              Restoring Dignity • Breaking Poverty Cycles
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
              Every Person Deserves Dignity
            </h2>
            <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto">
              Join us in our mission to break the cycle of extreme poverty in Kayunga District. 
              Your support provides immediate relief while building long-term solutions 
              that transform entire communities.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button 
                onClick={() => window.location.href = '/fund-our-work'}
                className="bg-white text-red-600 px-10 py-4 rounded-2xl font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Support This Mission
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Dress;